import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ExternalLink } from 'lucide-react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Use dark theme (light text on dark background) - default is light theme */
  darkMode?: boolean;
}

export function MarkdownRenderer({ content, className = '', darkMode = false }: MarkdownRendererProps) {
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const copyToClipboard = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Color classes based on theme
  const colors = darkMode ? {
    heading1: 'text-white',
    heading2: 'text-white',
    heading3: 'text-gray-100',
    heading4: 'text-gray-200',
    paragraph: 'text-gray-300',
    link: 'text-blue-400 hover:text-blue-300',
    list: 'text-gray-300',
    listItem: 'text-gray-300',
    blockquoteBg: 'bg-blue-900/30',
    blockquoteBorder: 'border-blue-400',
    blockquoteText: 'text-gray-300',
    tableBorder: 'border-gray-600',
    tableHeaderBg: 'bg-gray-800',
    tableHeaderText: 'text-gray-400',
    tableRowBg: 'bg-gray-900',
    tableRowHover: 'hover:bg-gray-800',
    tableCellText: 'text-gray-300',
    hr: 'border-gray-600',
    inlineCodeBg: 'bg-gray-700',
    inlineCodeText: 'text-gray-200',
    strong: 'text-white',
    emphasis: 'text-gray-300',
  } : {
    heading1: 'text-gray-900',
    heading2: 'text-gray-900',
    heading3: 'text-gray-900',
    heading4: 'text-gray-800',
    paragraph: 'text-gray-700',
    link: 'text-blue-600 hover:text-blue-800',
    list: 'text-gray-700',
    listItem: 'text-gray-700',
    blockquoteBg: 'bg-blue-50',
    blockquoteBorder: 'border-blue-500',
    blockquoteText: 'text-gray-700',
    tableBorder: 'border-gray-200',
    tableHeaderBg: 'bg-gray-100',
    tableHeaderText: 'text-gray-600',
    tableRowBg: 'bg-white',
    tableRowHover: 'hover:bg-gray-50',
    tableCellText: 'text-gray-700',
    hr: 'border-gray-200',
    inlineCodeBg: 'bg-gray-100',
    inlineCodeText: 'text-gray-800',
    strong: 'text-gray-900',
    emphasis: 'text-gray-700',
  };

  // Container classes - no prose classes since @tailwindcss/typography is not installed
  // All styling is handled by custom components below
  const containerClasses = 'max-w-none';

  return (
    <div className={`${containerClasses} ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className={`text-2xl font-bold ${colors.heading1} mt-6 mb-4 pb-2 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={`text-xl font-semibold ${colors.heading2} mt-5 mb-3`}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={`text-lg font-medium ${colors.heading3} mt-4 mb-2`}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className={`text-base font-medium ${colors.heading4} mt-3 mb-2`}>
              {children}
            </h4>
          ),

          // Paragraphs
          p: ({ children }) => (
            <p className={`${colors.paragraph} leading-relaxed mb-4`}>
              {children}
            </p>
          ),

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${colors.link} hover:underline inline-flex items-center gap-1`}
            >
              {children}
              <ExternalLink className="w-3 h-3" />
            </a>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className={`list-disc list-inside space-y-1 mb-4 ${colors.list}`}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal list-inside space-y-1 mb-4 ${colors.list}`}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className={colors.listItem}>{children}</li>
          ),

          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className={`border-l-4 ${colors.blockquoteBorder} pl-4 py-2 my-4 ${colors.blockquoteBg} rounded-r ${colors.blockquoteText} italic`}>
              {children}
            </blockquote>
          ),

          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className={`min-w-full divide-y ${colors.tableBorder} border ${colors.tableBorder} rounded-lg`}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className={colors.tableHeaderBg}>{children}</thead>
          ),
          tbody: ({ children }) => (
            <tbody className={`divide-y ${colors.tableBorder} ${colors.tableRowBg}`}>{children}</tbody>
          ),
          tr: ({ children }) => (
            <tr className={colors.tableRowHover}>{children}</tr>
          ),
          th: ({ children }) => (
            <th className={`px-4 py-2 text-left text-xs font-medium ${colors.tableHeaderText} uppercase tracking-wider`}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={`px-4 py-2 text-sm ${colors.tableCellText}`}>{children}</td>
          ),

          // Horizontal rule
          hr: () => <hr className={`my-6 ${colors.hr}`} />,

          // Inline code
          code: ({ className: codeClassName, children }) => {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const codeString = String(children).replace(/\n$/, '');
            
            // Check if it's a code block (has language) or inline code
            const isCodeBlock = match || (codeString.includes('\n'));

            if (isCodeBlock) {
              const language = match ? match[1] : 'text';
              
              return (
                <div className="relative group my-4">
                  <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button
                      onClick={() => copyToClipboard(codeString)}
                      className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white transition-colors"
                      title="Copiar código"
                    >
                      {copiedCode === codeString ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 bg-gray-800 px-3 py-1 rounded-t border-b border-gray-700">
                    {language}
                  </div>
                  <SyntaxHighlighter
                    style={oneDark}
                    language={language}
                    PreTag="div"
                    customStyle={{
                      margin: 0,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      fontSize: '0.875rem',
                    }}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                </div>
              );
            }

            // Inline code
            return (
              <code className={`px-1.5 py-0.5 ${colors.inlineCodeBg} ${colors.inlineCodeText} rounded text-sm font-mono`}>
                {children}
              </code>
            );
          },

          // Pre (for code blocks without language)
          pre: ({ children }) => {
            // If children is already processed by code component, just return it
            if (React.isValidElement(children) && children.type === 'div') {
              return children;
            }
            return (
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 text-sm">
                {children}
              </pre>
            );
          },

          // Strong/Bold
          strong: ({ children }) => (
            <strong className={`font-semibold ${colors.strong}`}>{children}</strong>
          ),

          // Emphasis/Italic
          em: ({ children }) => (
            <em className={`italic ${colors.emphasis}`}>{children}</em>
          ),

          // Images
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="max-w-full h-auto rounded-lg shadow-sm my-4"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownRenderer;
